import React, { useCallback, useContext, useEffect, } from 'react';
import styled from 'styled-components';
import * as icons from '@boxine/tonies-ui/icons';
import { useTranslation } from 'react-i18next';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import { AssistiveTechnologyOnly } from '../../../styles/AssistiveTechnologyOnly';
import Icon from '../../Icon';
import { MainNavigationContext } from '../MainNavigationContext';
import { useCloseOnEscape } from '../../../hooks/useCloseOnEscape';
import { HEIGHT_VIEWBOX_SMALL_SWUNG_IN_PX } from './SwungBorder';
const ID_MOBILE_NAVIGATION_LABEL = 'main-navigation-mobile-navigation-label';
const ID_MOBILE_MENU = 'main-navigation-mobile-navigation';
const VERTICAL_PADDING_HAMBURGER_MENU = '3.5rem';
const HamburgerIconButton = styled.button `
  background: transparent;
  border: none;
  cursor: pointer;
  margin: 0;
  margin-left: 1rem;
  padding: 0;
  color: ${props => props.theme.colors.darkergrey};
`;
const StyledIcon = styled(Icon) `
  pointer-events: none;
  height: auto;
  width: 1.5rem;
`;
const HamburgerIcon = ({ dataTestId = undefined, isOpen, onClick, 'data-trackingid': dataTrackingId = undefined, }) => {
    const { t } = useTranslation('MainNavigation');
    return (React.createElement(HamburgerIconButton, { "aria-controls": ID_MOBILE_MENU, "aria-expanded": isOpen, "data-testid": dataTestId, "data-trackingid": dataTrackingId, onClick: onClick },
        React.createElement(StyledIcon, { title: t('Menu'), type: isOpen ? icons.closeX : icons.menu })));
};
const HamburgerMenu = styled.div `
  background-color: ${props => props.theme.White};
  display: ${props => (props.isOpen ? 'block' : 'none')};
  height: 100vh;
  padding-top: calc(
    ${VERTICAL_PADDING_HAMBURGER_MENU} + ${HEIGHT_VIEWBOX_SMALL_SWUNG_IN_PX}px
  );
  position: absolute;
  right: 0;
  top: 0;
  width: 100vw;
  overflow: ${props => (props.isNestedNavigationOpen ? 'visible' : 'auto')};
  /* Show under header bar */
  z-index: -1;

  /* Fix for padding-bottom not respected by overflow: auto */
  &:after {
    content: '';
    display: block;
    height: calc(
      ${VERTICAL_PADDING_HAMBURGER_MENU} + ${HEIGHT_VIEWBOX_SMALL_SWUNG_IN_PX}px
    );
    width: 100%;
  }
`;
const HorizontalLine = styled.div `
  background-color: ${props => props.theme.GrayLightest};
  height: 0.125rem;
  margin: 3rem auto
    ${props => props.isLoggedIn && !props.hasHamburgerMenuLoginOutSlot
    ? '3.rem'
    : '5rem'};

  @supports (margin: clamp(1rem, 10vh, 1rem)) {
    margin-top: clamp(1.5rem, 5vh, 6rem);
    margin-bottom: ${props => props.isLoggedIn && !props.hasHamburgerMenuLoginOutSlot
    ? 'clamp(0.8rem, 4vh, 4rem)'
    : 'clamp(1.5rem, 5vh, 6rem)'};
  }

  width: 14rem;
`;
const VisuallyHiddenLabel = styled.h2 `
  ${AssistiveTechnologyOnly}
`;
const SecondaryItemsWrapper = styled.div `
  display: ${props => (props.isDisplayed ? 'block' : 'none')};
`;
const SERVER_SIDE_ROUTING = 'server-side-routing';
export const MobileNavigation = ({ isLoggedIn, isMobileMenuOpen, onChangeIsMobileMenuOpen, primaryItems, secondaryItems, hamburgerMenuLoginOutSlot = null, 'data-trackingid': dataTrackingId, }) => {
    const { isNestedNavigationOpen, setShouldFocusNestedNavigation, setIsNestedNavigationOpen, } = useContext(MainNavigationContext);
    const { t } = useTranslation('MainNavigation');
    let location;
    try {
        location = window.location.pathname;
    }
    catch (_a) {
        location = SERVER_SIDE_ROUTING;
    }
    // Close menu after navigation
    useEffect(() => {
        setShouldFocusNestedNavigation(true);
        onChangeIsMobileMenuOpen(false);
    }, [onChangeIsMobileMenuOpen, location, setShouldFocusNestedNavigation]);
    // Close mobile menu when pressing Escape
    const closeOnEscape = useCallback(() => {
        if (isNestedNavigationOpen) {
            setIsNestedNavigationOpen(false);
        }
        else {
            setShouldFocusNestedNavigation(true);
            onChangeIsMobileMenuOpen(false);
        }
    }, [
        isNestedNavigationOpen,
        onChangeIsMobileMenuOpen,
        setIsNestedNavigationOpen,
        setShouldFocusNestedNavigation,
    ]);
    useCloseOnEscape(closeOnEscape);
    /**
     * If the page content (= anything besides the MainNavigation) requires a
     * scrollbar, set the width to 100% otherwise this behaves as if "overflow"
     * was set to "scroll".
     */
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.width = '100%';
        }
        else {
            document.body.style.width = 'auto';
        }
    }, [isMobileMenuOpen]);
    return (React.createElement("nav", { "aria-labelledby": ID_MOBILE_NAVIGATION_LABEL, "data-testid": "main-navigation-mobile-navigation" },
        React.createElement(VisuallyHiddenLabel, { id: ID_MOBILE_NAVIGATION_LABEL }, t('MobileNavigationLabel')),
        React.createElement(FocusLock, { disabled: !isMobileMenuOpen, returnFocus: true },
            React.createElement(RemoveScroll, { enabled: isMobileMenuOpen },
                React.createElement(HamburgerIcon, { dataTestId: "main-navigation-mobile-toggle", "data-trackingid": dataTrackingId, isOpen: isMobileMenuOpen, onClick: () => {
                        if (isMobileMenuOpen) {
                            setShouldFocusNestedNavigation(true);
                            setIsNestedNavigationOpen(false);
                        }
                        onChangeIsMobileMenuOpen(!isMobileMenuOpen);
                    } }),
                React.createElement(HamburgerMenu, { id: ID_MOBILE_MENU, isNestedNavigationOpen: isNestedNavigationOpen, isOpen: isMobileMenuOpen, tabIndex: -1 },
                    primaryItems,
                    React.createElement(HorizontalLine, { isLoggedIn: isLoggedIn, hasHamburgerMenuLoginOutSlot: Boolean(hamburgerMenuLoginOutSlot) }),
                    React.createElement(SecondaryItemsWrapper, { isDisplayed: !isNestedNavigationOpen }, secondaryItems),
                    hamburgerMenuLoginOutSlot && (React.createElement(HorizontalLine, { isLoggedIn: isLoggedIn, hasHamburgerMenuLoginOutSlot: Boolean(hamburgerMenuLoginOutSlot) })),
                    hamburgerMenuLoginOutSlot)))));
};
